import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import type { PlayerGoal } from "@/lib/types";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { useMyGoalReflection } from "../hooks/useMyGoalReflection";
import { viewerGoalContextLabel } from "./GoalCard";

/**
 * Viewer goal-reflection modal (M7) — opened from a goal card's
 * "Add reflection" button on the `/feedback` Development tab.
 * Ports the legacy `openGoalReflectionModal`
 * (`js/coaching/goals.js`).
 *
 * Shows the goal card read-only (`actions={false}`) for context,
 * then a reflection textarea. Submit → `POST /api/my-feedback/
 * goals/{id}/reflection`; on success the modal closes and the
 * hook invalidates the feedback + development queries so the new
 * "Latest reflection" appears on the card.
 *
 * Reuses `CoachModal` as the portal shell — it's a generic
 * one-modal-at-a-time portal, not coach-specific.
 */

export type GoalReflectionModalProps = {
  goal: PlayerGoal;
  onClose: () => void;
  /** Called after a reflection saves (the host may refetch). */
  onSaved?: () => void;
};

const MAX_REFLECTION_LENGTH = 1000;

export function GoalReflectionModal({
  goal,
  onClose,
  onSaved,
}: GoalReflectionModalProps) {
  const toast = useToast();
  const [reflection, setReflection] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { submitReflection, isPending } = useMyGoalReflection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = reflection.trim();
    if (!text) {
      setError("Add a reflection before saving.");
      return;
    }
    setError(null);
    try {
      await submitReflection(goal.id, text);
      toast.success("Reflection submitted");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : "Could not save your reflection.",
      );
      toast.error("Couldn't submit reflection", {
        description: errorMessage(err),
      });
    }
  };

  return (
    <CoachModal
      title="Reflect on your goal"
      onClose={onClose}
      data-testid="goal-reflection-modal"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button
            type="submit"
            form="goal-reflection-form"
            disabled={isPending}
            data-testid="goal-reflection-submit"
          >
            {isPending ? "Saving…" : "Save reflection"}
            <ArrowUpRight size={17} aria-hidden />
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <div className="goal-reflection-content flex flex-col gap-4">
        <section className="goal-reflection-context">
          <p className="eyebrow">{viewerGoalContextLabel(goal.context)}</p>
          <h3>{goal.title || "Player goal"}</h3>
          {goal.description ? <p>{goal.description}</p> : null}
        </section>

        <form
          id="goal-reflection-form"
          className="flex flex-col gap-3"
          onSubmit={handleSubmit}
        >
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-ink">
              Your reflection <span aria-hidden="true">*</span>
            </span>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              maxLength={MAX_REFLECTION_LENGTH}
              rows={4}
              autoFocus
              data-testid="goal-reflection-text"
              placeholder="What did you try? What should your coach know?"
              className="w-full bg-field border-0 rounded px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>

          <p className="text-sm text-ink-muted">
            Shared with your coach. This adds a reflection to your goal.
          </p>

          {error ? (
            <p
              className="text-destructive font-mono text-xs"
              data-testid="goal-reflection-error"
            >
              {error}
            </p>
          ) : null}

        </form>
      </div>
    </CoachModal>
  );
}
